import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { ShoppingCart, User, Menu, X, LogOut, Settings, Package, MessageSquare, BookOpen, Heart, HandHeart } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [bookSubcategories, setBookSubcategories] = useState([]);
  const { user, isAuthenticated, isAdmin, logout } = useAuth();
  const { cartCount } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catRes, subCatRes] = await Promise.all([
          axios.get(`${API}/categories`),
          axios.get(`${API}/book-subcategories`)
        ]);
        setCategories(catRes.data);
        setBookSubcategories(subCatRes.data);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      }
    };
    fetchData();
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <header className="sticky top-0 z-50 glass-header border-b border-border" data-testid="header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group flex-shrink-0" data-testid="logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_brookzone/artifacts/o226qsqt_ChatGPT%20Image%20Jan%204%2C%202026%2C%2007_21_15%20PM%20%281%29.png" 
              alt="Brookzone Bookseller" 
              className="h-8 w-8 sm:h-10 sm:w-10 object-contain transition-transform group-hover:scale-110"
            />
            <span className="font-serif text-sm sm:text-xl font-bold text-primary">
              Brookzone Bookseller
            </span>
          </Link>

          {/* Desktop Navigation - Three Dropdowns */}
          <nav className="hidden md:flex items-center gap-2" data-testid="desktop-nav">
            {/* Books Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="text-foreground hover:text-primary" data-testid="books-dropdown">
                  Books
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-48">
                <DropdownMenuItem asChild>
                  <Link to="/category/books" className="w-full cursor-pointer font-medium" data-testid="all-books-link">
                    All Books
                  </Link>
                </DropdownMenuItem>
                {bookSubcategories.length > 0 && <DropdownMenuSeparator />}
                {bookSubcategories.map((subcat) => (
                  <DropdownMenuItem key={subcat.id} asChild>
                    <Link 
                      to={`/books/${subcat.name.toLowerCase().replace(/\s+/g, '-')}`} 
                      className="w-full cursor-pointer"
                    >
                      {subcat.name}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* All Products Dropdown - excludes E-books since they have their own section */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="text-foreground hover:text-primary" data-testid="products-dropdown">
                  Products
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-48">
                <DropdownMenuItem asChild>
                  <Link to="/products" className="w-full cursor-pointer font-medium" data-testid="all-products-link">
                    Shop All
                  </Link>
                </DropdownMenuItem>
                {categories.length > 0 && <DropdownMenuSeparator />}
                {categories.filter(cat => cat.name.toLowerCase() !== 'e-books' && cat.name.toLowerCase() !== 'ebooks').map((cat) => (
                  <DropdownMenuItem key={cat.id} asChild>
                    <Link 
                      to={`/category/${cat.name.toLowerCase().replace(/\s+/g, '-')}`} 
                      className="w-full cursor-pointer"
                    >
                      {cat.name}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* E-books Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="text-foreground hover:text-primary" data-testid="ebooks-nav-link">
                  E-books
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-48">
                <DropdownMenuItem asChild>
                  <Link to="/ebooks" className="w-full cursor-pointer font-medium">
                    All E-books
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/ebooks?filter=ebook" className="w-full cursor-pointer">
                    E-books
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/ebooks?filter=guide" className="w-full cursor-pointer">
                    Guides
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/ebooks?filter=workbook" className="w-full cursor-pointer">
                    Workbooks
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/ebooks?filter=journal" className="w-full cursor-pointer">
                    Journals
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* About Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="text-foreground hover:text-primary" data-testid="about-dropdown">
                  About
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-48">
                <DropdownMenuItem asChild>
                  <Link to="/about" className="w-full cursor-pointer">
                    Our Story
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/contact" className="w-full cursor-pointer">
                    Contact Us
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/blog" className="w-full cursor-pointer">
                    Journal / Blog
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/subscribe" className="w-full cursor-pointer">
                    Newsletter
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/donate" className="w-full cursor-pointer font-medium text-primary">
                    <HandHeart className="h-4 w-4 mr-2" />
                    Donate
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>

          {/* Right side actions */}
          <div className="flex items-center gap-4">
            {/* Cart */}
            {isAuthenticated && (
              <Link to="/cart" className="relative" data-testid="cart-link">
                <Button variant="ghost" size="icon" className="relative">
                  <ShoppingCart className="h-5 w-5" />
                  {cartCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-accent text-accent-foreground text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center" data-testid="cart-count">
                      {cartCount}
                    </span>
                  )}
                </Button>
              </Link>
            )}

            {/* User menu */}
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" data-testid="user-menu-trigger">
                    <User className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user?.name}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/orders" className="w-full cursor-pointer" data-testid="orders-link">
                      <Package className="mr-2 h-4 w-4" />
                      My Orders
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/messages" className="w-full cursor-pointer" data-testid="messages-link">
                      <MessageSquare className="mr-2 h-4 w-4" />
                      My Messages
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/my-library" className="w-full cursor-pointer" data-testid="my-library-link">
                      <BookOpen className="mr-2 h-4 w-4" />
                      My Library
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/wishlist" className="w-full cursor-pointer" data-testid="wishlist-link">
                      <Heart className="mr-2 h-4 w-4" />
                      My Wishlist
                    </Link>
                  </DropdownMenuItem>
                  {isAdmin && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link to="/admin" className="w-full cursor-pointer" data-testid="admin-link">
                          <Settings className="mr-2 h-4 w-4" />
                          Admin Panel
                        </Link>
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-destructive" data-testid="logout-btn">
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login">
                  <Button variant="ghost" data-testid="login-btn">Login</Button>
                </Link>
                <Link to="/register">
                  <Button className="btn-primary" data-testid="register-btn">Sign Up</Button>
                </Link>
              </div>
            )}

            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-3 sm:py-4 border-t border-border animate-fade-in max-h-[70vh] overflow-y-auto touch-scroll" data-testid="mobile-menu">
            <nav className="flex flex-col gap-1">
              {/* E-books Section - Prominent at top */}
              <p className="px-4 py-1.5 text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase">E-books & Digital</p>
              <Link
                to="/ebooks"
                className="px-4 py-2.5 text-foreground hover:bg-muted rounded-md transition-colors font-medium flex items-center gap-2 text-sm"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-ebooks-link"
              >
                <BookOpen className="h-4 w-4" />
                All E-books
              </Link>
              <Link
                to="/ebooks?filter=ebook"
                className="px-6 py-2 text-foreground hover:bg-muted rounded-md transition-colors text-sm"
                onClick={() => setMobileMenuOpen(false)}
              >
                E-books
              </Link>
              <Link
                to="/ebooks?filter=guide"
                className="px-6 py-2 text-foreground hover:bg-muted rounded-md transition-colors text-sm"
                onClick={() => setMobileMenuOpen(false)}
              >
                Guides
              </Link>
              <Link
                to="/ebooks?filter=workbook"
                className="px-6 py-2 text-foreground hover:bg-muted rounded-md transition-colors text-sm"
                onClick={() => setMobileMenuOpen(false)}
              >
                Workbooks
              </Link>
              <Link
                to="/ebooks?filter=journal"
                className="px-6 py-2 text-foreground hover:bg-muted rounded-md transition-colors text-sm"
                onClick={() => setMobileMenuOpen(false)}
              >
                Journals
              </Link>

              <DropdownMenuSeparator className="my-2" />
              <p className="px-4 py-1.5 text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase">Books</p>
              <Link
                to="/category/books"
                className="px-4 py-2.5 text-foreground hover:bg-muted rounded-md transition-colors font-medium text-sm"
                onClick={() => setMobileMenuOpen(false)}
              >
                All Books
              </Link>
              {bookSubcategories.map((subcat) => (
                <Link
                  key={subcat.id}
                  to={`/books/${subcat.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className="px-6 py-2 text-foreground hover:bg-muted rounded-md transition-colors text-sm"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {subcat.name}
                </Link>
              ))}
              
              <DropdownMenuSeparator className="my-2" />
              <p className="px-4 py-1.5 text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase">Shop</p>
              <Link
                to="/products"
                className="px-4 py-2.5 text-foreground hover:bg-muted rounded-md transition-colors font-medium text-sm"
                onClick={() => setMobileMenuOpen(false)}
              >
                All Products
              </Link>
              {categories.filter(cat => cat.name.toLowerCase() !== 'e-books' && cat.name.toLowerCase() !== 'ebooks').map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className="px-6 py-2 text-foreground hover:bg-muted rounded-md transition-colors text-sm"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {cat.name}
                </Link>
              ))}
              
              <DropdownMenuSeparator className="my-2" />
              <p className="px-4 py-1.5 text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase">About</p>
              <Link
                to="/about"
                className="px-4 py-2.5 text-foreground hover:bg-muted rounded-md transition-colors text-sm"
                onClick={() => setMobileMenuOpen(false)}
              >
                Our Story
              </Link>
              <Link
                to="/contact"
                className="px-4 py-2.5 text-foreground hover:bg-muted rounded-md transition-colors text-sm"
                onClick={() => setMobileMenuOpen(false)}
              >
                Contact Us
              </Link>
              <Link
                to="/blog"
                className="px-4 py-2.5 text-foreground hover:bg-muted rounded-md transition-colors text-sm"
                onClick={() => setMobileMenuOpen(false)}
              >
                Journal / Blog
              </Link>
              <Link
                to="/subscribe"
                className="px-4 py-2.5 text-foreground hover:bg-muted rounded-md transition-colors text-sm"
                onClick={() => setMobileMenuOpen(false)}
              >
                Newsletter
              </Link>
              
              <DropdownMenuSeparator className="my-2" />
              <Link
                to="/donate"
                className="px-4 py-2.5 text-primary font-medium hover:bg-primary/10 rounded-md transition-colors flex items-center gap-2 text-sm"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-donate-link"
              >
                <HandHeart className="h-4 w-4" />
                Donate & Support
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};
